import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Navbar,
  Form,
  Button,
  Card,
  Tab,
  Nav,
  Spinner,
  Alert,
  Badge,
  Table,
} from 'react-bootstrap';
import { useTheme } from '../utils/ThemeContext';
import {
  getAllUsersAdmin,
  getAllErrorReportsAdmin,
  findSessionOwnerAdmin,
} from '../utils/firebase';

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function shortUid(uid) {
  if (!uid) return '—';
  return uid.length > 10 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid;
}

// Build an admin URL for a session/owner pair. Origin defaults to the current
// window origin but can be overridden so the same builder is usable elsewhere.
export function buildAdminSessionUrl(ownerUid, sessionId, origin) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/admin/user/${ownerUid}/session/${sessionId}/chat`;
}

function AdminPage() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  const [activeTab, setActiveTab] = useState('search');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [errorReports, setErrorReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState(null);

  const [sessionQuery, setSessionQuery] = useState('');
  const [sessionResult, setSessionResult] = useState(null);
  const [sessionSearching, setSessionSearching] = useState(false);
  const [sessionSearchError, setSessionSearchError] = useState(null);

  const [expandedUid, setExpandedUid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const list = await getAllUsersAdmin();
        if (!cancelled) setUsers(list);
      } catch (err) {
        if (!cancelled) setUsersError(err.message || 'Failed to load users');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    const loadReports = async () => {
      setReportsLoading(true);
      setReportsError(null);
      try {
        const list = await getAllErrorReportsAdmin();
        if (!cancelled) setErrorReports(list);
      } catch (err) {
        if (!cancelled) setReportsError(err.message || 'Failed to load error reports');
      } finally {
        if (!cancelled) setReportsLoading(false);
      }
    };
    loadUsers();
    loadReports();
    return () => { cancelled = true; };
  }, []);

  // First check the already-loaded users data; fall back to a server scan if
  // the page was opened before users finished loading.
  const handleSessionSearch = async (e) => {
    e?.preventDefault();
    const id = sessionQuery.trim();
    setSessionResult(null);
    setSessionSearchError(null);
    if (!id) return;
    setSessionSearching(true);
    try {
      let ownerUid = null;
      for (const u of users) {
        if (u.sessions.some((s) => s.id === id)) {
          ownerUid = u.uid;
          break;
        }
      }
      if (!ownerUid) ownerUid = await findSessionOwnerAdmin(id);
      if (ownerUid) {
        setSessionResult({ ownerUid, sessionId: id });
      } else {
        setSessionSearchError(`No session found with ID "${id}"`);
      }
    } catch (err) {
      setSessionSearchError(err.message || 'Search failed');
    } finally {
      setSessionSearching(false);
    }
  };

  // Build adminUrl for an error report; uses stored sessionId when present,
  // otherwise falls back to deriving from the report's url (path matching).
  const adminUrlForReport = (report) => {
    const ownerUid = report.sessionOwnerUid || report.uid;
    if (report.sessionId) return buildAdminSessionUrl(ownerUid, report.sessionId);
    try {
      const parsed = new URL(report.url || '', window.location.origin);
      const m = parsed.pathname.match(/^\/session\/([^/]+)/);
      if (m && m[1] !== 'new' && !m[1].startsWith('temp_')) {
        return buildAdminSessionUrl(report.uid, m[1]);
      }
      const s = parsed.pathname.match(/^\/shared\/([^/]+)\/([^/]+)/);
      if (s) return buildAdminSessionUrl(s[1], s[2]);
    } catch {
      // ignore
    }
    return null;
  };

  const totalSessions = useMemo(
    () => users.reduce((acc, u) => acc + u.sessionCount, 0),
    [users]
  );

  const navigateToSession = (ownerUid, sessionId) => {
    navigate(`/admin/user/${ownerUid}/session/${sessionId}/chat`);
  };

  return (
    <div className="app-wrapper d-flex flex-column vh-100">
      <Navbar variant={resolvedTheme === 'dark' ? 'dark' : 'light'} className="app-navbar">
        <Container fluid>
          <Navbar.Brand
            className="d-flex align-items-center"
            role="button"
            onClick={() => navigate('/')}
          >
            <img src="/favicon-32x32.png" alt="Plum" style={{ width: 28, height: 28, marginRight: 8 }} />
            <span>Plum Admin</span>
          </Navbar.Brand>
          <Badge bg="warning" text="dark">
            <i className="bi bi-shield-lock me-1"></i>Admin
          </Badge>
        </Container>
      </Navbar>

      <div className="flex-grow-1 overflow-auto p-3 p-md-4">
        <Container>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'search')}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="search">
                  <i className="bi bi-search me-1"></i>Session Search
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="users">
                  <i className="bi bi-people me-1"></i>Users
                  {users.length > 0 && (
                    <Badge bg="secondary" className="ms-1">{users.length}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="errors">
                  <i className="bi bi-exclamation-triangle me-1"></i>Error Reports
                  {errorReports.length > 0 && (
                    <Badge bg="danger" className="ms-1">{errorReports.length}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* --- Session Search --- */}
              <Tab.Pane eventKey="search">
                <Card>
                  <Card.Body>
                    <Card.Title>Look up a session by ID</Card.Title>
                    <Card.Text className="text-muted">
                      Paste a session ID to get an admin link to that session.
                    </Card.Text>
                    <Form onSubmit={handleSessionSearch} className="d-flex gap-2 mb-3">
                      <Form.Control
                        type="text"
                        value={sessionQuery}
                        onChange={(e) => setSessionQuery(e.target.value)}
                        placeholder="Session ID (e.g. -ABC123...)"
                        autoFocus
                      />
                      <Button type="submit" disabled={sessionSearching || !sessionQuery.trim()}>
                        {sessionSearching ? <Spinner size="sm" animation="border" /> : 'Search'}
                      </Button>
                    </Form>

                    {sessionSearchError && (
                      <Alert variant="warning" className="mb-0">{sessionSearchError}</Alert>
                    )}

                    {sessionResult && (
                      <Alert variant="success" className="mb-0">
                        <div className="mb-2">
                          <strong>Found.</strong> Owner UID:{' '}
                          <code>{sessionResult.ownerUid}</code>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => navigateToSession(sessionResult.ownerUid, sessionResult.sessionId)}
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>Open as admin
                          </Button>
                          <Form.Control
                            readOnly
                            value={buildAdminSessionUrl(sessionResult.ownerUid, sessionResult.sessionId)}
                            onClick={(e) => e.target.select()}
                            style={{ minWidth: 320, flex: 1 }}
                          />
                        </div>
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* --- Users --- */}
              <Tab.Pane eventKey="users">
                <Card>
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <Card.Title className="mb-0">All users</Card.Title>
                      <small className="text-muted">
                        {users.length} users · {totalSessions} sessions
                      </small>
                    </div>

                    {usersLoading && (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    )}
                    {usersError && (
                      <Alert variant="danger">{usersError}</Alert>
                    )}
                    {!usersLoading && !usersError && users.length === 0 && (
                      <p className="text-muted mb-0">No users found.</p>
                    )}

                    {!usersLoading && !usersError && users.length > 0 && (
                      <Table responsive hover size="sm">
                        <thead>
                          <tr>
                            <th>UID</th>
                            <th>Sessions</th>
                            <th>Last activity</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => (
                            <React.Fragment key={u.uid}>
                              <tr>
                                <td>
                                  <code title={u.uid}>{shortUid(u.uid)}</code>
                                </td>
                                <td>{u.sessionCount}</td>
                                <td>{formatDate(u.lastActivity)}</td>
                                <td>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() =>
                                      setExpandedUid(expandedUid === u.uid ? null : u.uid)
                                    }
                                  >
                                    {expandedUid === u.uid ? 'Hide' : 'Show'} sessions
                                  </Button>
                                </td>
                              </tr>
                              {expandedUid === u.uid && (
                                <tr>
                                  <td colSpan={4} className="bg-light">
                                    {u.sessions.length === 0 ? (
                                      <em className="text-muted">No sessions.</em>
                                    ) : (
                                      <Table size="sm" borderless className="mb-0">
                                        <thead>
                                          <tr>
                                            <th>Session ID</th>
                                            <th>Title</th>
                                            <th>Created</th>
                                            <th></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {u.sessions.map((s) => (
                                            <tr key={s.id}>
                                              <td><code>{s.id}</code></td>
                                              <td>{s.title || <em className="text-muted">untitled</em>}</td>
                                              <td>{formatDate(s.createdAt)}</td>
                                              <td>
                                                <Button
                                                  size="sm"
                                                  variant="outline-primary"
                                                  onClick={() => navigateToSession(u.uid, s.id)}
                                                >
                                                  Open
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* --- Error Reports --- */}
              <Tab.Pane eventKey="errors">
                <Card>
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <Card.Title className="mb-0">Error reports</Card.Title>
                      <small className="text-muted">{errorReports.length} reports</small>
                    </div>

                    {reportsLoading && (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    )}
                    {reportsError && (
                      <Alert variant="danger">{reportsError}</Alert>
                    )}
                    {!reportsLoading && !reportsError && errorReports.length === 0 && (
                      <p className="text-muted mb-0">No error reports.</p>
                    )}

                    {!reportsLoading && !reportsError && errorReports.length > 0 && (
                      <Table responsive hover size="sm">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Status</th>
                            <th>Mode</th>
                            <th>Message</th>
                            <th>UID</th>
                            <th>Admin link</th>
                          </tr>
                        </thead>
                        <tbody>
                          {errorReports.map((r) => {
                            const adminUrl = adminUrlForReport(r);
                            return (
                              <tr key={r.id}>
                                <td className="text-nowrap">{formatDate(r.timestamp)}</td>
                                <td>
                                  <Badge
                                    bg={
                                      r.status === 'new' ? 'danger' :
                                      r.status === 'processed' ? 'secondary' :
                                      r.status === 'skipped-dev' ? 'light' : 'info'
                                    }
                                    text={r.status === 'skipped-dev' ? 'dark' : undefined}
                                  >
                                    {r.status || 'unknown'}
                                  </Badge>
                                </td>
                                <td>{r.mode || '—'}</td>
                                <td style={{ maxWidth: 360 }}>
                                  <div className="text-truncate" title={r.message}>
                                    {r.message || <em className="text-muted">no message</em>}
                                  </div>
                                  <small className="text-muted">
                                    <code>{r.fingerprint}</code>
                                  </small>
                                </td>
                                <td><code title={r.uid}>{shortUid(r.uid)}</code></td>
                                <td>
                                  {adminUrl ? (
                                    <a href={adminUrl} target="_blank" rel="noreferrer">
                                      Open
                                    </a>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </div>
    </div>
  );
}

export default AdminPage;
